import { Component } from '@theme/component';

const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const aliases = { brussel: 'bruxelles', brussels: 'bruxelles', ghent: 'gent', gand: 'gent', liege: 'liege', luik: 'liege', anvers: 'antwerpen', antwerp: 'antwerpen', bruges: 'brugge', mons: 'mons', bergen: 'mons', namen: 'namur', mechelen: 'mechelen', malines: 'mechelen', louvain: 'leuven', luxemburg: 'luxembourg', denhaag: 's gravenhage', 'den haag': 's gravenhage', 'the hague': 's gravenhage', hertogenbosch: 's hertogenbosch', 'den bosch': 's hertogenbosch' };
const distance = (a, b) => {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};
const hasCoordinates = store => Number.isFinite(store.lat) && Number.isFinite(store.lng);
const safeLink = value => {
  try { const url = new URL(value); return /^https?:$/.test(url.protocol) ? url.href : ''; } catch { return ''; }
};

class GentleeStoreLocator extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.abort) return;
    this.abort = new AbortController();
    this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
    this.form = this.querySelector('form');
    this.input = this.querySelector('[name="place"]');
    this.country = this.querySelector('[name="country"]');
    this.kind = this.querySelector('[name="kind"]');
    this.status = this.querySelector('[data-status]');
    this.results = this.querySelector('[data-results]');
    this.choices = this.querySelector('[data-choices]');
    this.online = this.querySelector('[data-online]');
    this.mapHost = this.querySelector('[data-map]');
    this.limit = 20;
    this.generation = 0;
    const options = { signal: this.abort.signal };
    this.form.addEventListener('submit', event => { event.preventDefault(); this.search(); }, options);
    this.country.addEventListener('change', () => this.search(), options);
    this.kind.addEventListener('change', () => this.search(), options);
    this.querySelector('[data-reset]').addEventListener('click', () => { this.input.value = ''; this.country.value = ''; this.kind.value = ''; this.search(); this.input.focus(); }, options);
    this.querySelector('[data-more]').addEventListener('click', () => { this.limit += 20; this.renderCards(); }, options);
    this.querySelector('[data-retry]').addEventListener('click', () => this.load(), options);
    this.load();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.abort = null;
    this.map?.destroy();
    this.map = null;
    super.disconnectedCallback();
  }

  t(key, replacements = {}) {
    let value = this.messages[key] || key;
    for (const [name, replacement] of Object.entries(replacements)) value = value.replaceAll(`[${name}]`, replacement);
    return value;
  }

  node(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }

  async load() {
    this.status.textContent = this.t('loading');
    this.querySelector('[data-retry]').hidden = true;
    this.form.querySelector('[type="submit"]').disabled = true;
    try {
      const response = await fetch(this.dataset.storesUrl, { signal: this.abort.signal });
      if (!response.ok) throw new Error('Store data unavailable');
      const data = await response.json();
      this.stores = Array.isArray(data) ? data : data.stores;
      if (!Array.isArray(this.stores) || !this.stores.length) throw new Error('Store data empty');
      this.form.querySelector('[type="submit"]').disabled = false;
      this.renderOnline();
      await this.search();
      if (!this.map) {
        try {
          const { LocatorMap } = await import(this.dataset.mapModule);
          if (!this.isConnected) return;
          this.map = new LocatorMap(this.mapHost, {
            label: this.t('map_label'), zoomIn: this.t('zoom_in'), zoomOut: this.t('zoom_out'), reset: this.t('map_reset'), mapUnavailable: this.t('map_unavailable'),
            onSelect: id => this.selectStore(id),
            onError: () => { this.querySelector('[data-map-note]').textContent = this.t('map_unavailable'); }
          });
          this.updateMap();
        } catch { this.querySelector('[data-map-note]').textContent = this.t('map_unavailable'); }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.status.textContent = this.t('load_error');
      this.querySelector('[data-retry]').hidden = false;
    }
  }

  async getPlaces() {
    if (this.places) return this.places;
    const response = await fetch(this.dataset.postcodesUrl, { signal: this.abort.signal });
    if (!response.ok) throw new Error('Place data unavailable');
    const data = await response.json();
    this.places = Object.entries(data.countries).flatMap(([country, rows]) => rows.map(([postcode, city, lat, lng]) => ({ country, postcode, city, lat, lng, normalized: normalize(city) })));
    return this.places;
  }

  async search() {
    if (!this.stores) return;
    const generation = ++this.generation;
    const query = normalize(this.input.value);
    const country = this.country.value;
    const kind = this.kind.value;
    this.origin = null;
    this.limit = 20;
    this.choices.replaceChildren();
    this.choices.hidden = true;
    this.filtered = this.stores.filter(store => store.kind !== 'online' && (!country || store.country === country) && (!kind || store.kind === kind));
    this.results.setAttribute('aria-busy', 'true');
    if (!query) { this.showResults(); return; }
    this.status.textContent = this.t('searching');
    try {
      const places = await this.getPlaces();
      if (generation !== this.generation) return;
      const postal = query.match(/^(?:l\s*)?(\d{4})(?:\s*([a-z]{2})(?=\s|$))?(?:\s+(.*))?$/);
      const numeric = Boolean(postal);
      const town = postal ? normalize(postal[3] || '') : query;
      const matchesTown = place => place.normalized === town || place.normalized === (place.country === 'BE' ? aliases[town] : (town === 'den haag' || town === 'den bosch' || town === 'the hague') ? aliases[town] : town);
      let matches = places.filter(place => (!country || place.country === country) && (numeric ? place.postcode === postal[1] && (!postal[2] || place.country === 'NL') && (!town || matchesTown(place)) : matchesTown(place)));
      if (!matches.length && !numeric && query.length >= 3) matches = places.filter(place => (!country || place.country === country) && place.normalized.startsWith(query));
      const groups = new Map();
      for (const place of matches) {
        const key = `${place.country}:${place.normalized}`;
        if (!groups.has(key)) groups.set(key, []);
        const clusters = groups.get(key);
        const cluster = clusters.find(rows => distance(rows[0], place) < 12);
        if (cluster) cluster.push(place); else clusters.push([place]);
      }
      const candidates = [...groups.values()].flat().map(rows => ({ ...rows[0], lat: rows.reduce((sum, row) => sum + row.lat, 0) / rows.length, lng: rows.reduce((sum, row) => sum + row.lng, 0) / rows.length }));
      if (candidates.length === 1) { this.origin = candidates[0]; this.showResults(); return; }
      if (candidates.length > 1) {
        this.status.textContent = this.t('choose_place');
        this.choices.hidden = false;
        for (const place of candidates.slice(0, 12)) {
          const button = this.node('button', 'gl-choice', `${place.city} · ${place.postcode} · ${this.t(place.country.toLowerCase())}`);
          button.type = 'button';
          button.addEventListener('click', () => { this.origin = place; this.choices.replaceChildren(); this.choices.hidden = true; this.showResults(); });
          this.choices.append(button);
        }
        if (candidates.length > 12) this.choices.append(this.node('p', '', this.t('more_specific')));
        this.matches = [];
        this.renderCards();
        this.map?.setStores([]);
        this.results.setAttribute('aria-busy', 'false');
        return;
      }
    } catch (error) {
      if (error.name === 'AbortError' || generation !== this.generation) return;
      this.geoError = true;
    }
    this.filtered = this.filtered.filter(store => normalize(`${store.name} ${store.city} ${store.postcode} ${store.address}`).includes(query));
    this.showResults(this.geoError ? this.t('place_load_error') : '');
    this.geoError = false;
  }

  showResults(note = '') {
    this.matches = this.filtered.map(store => ({ ...store, distance: this.origin && hasCoordinates(store) ? distance(this.origin, store) : null }));
    if (this.origin) this.matches.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    else this.matches.sort((a, b) => `${a.city} ${a.name}`.localeCompare(`${b.city} ${b.name}`, this.dataset.locale));
    this.status.textContent = note || (this.matches.length ? (this.origin ? this.t('near_results', { count: this.matches.length, place: this.origin.city }) : this.t('results', { count: this.matches.length })) : this.t('no_results'));
    this.results.setAttribute('aria-busy', 'false');
    this.renderCards();
    this.updateMap();
  }

  updateMap() {
    if (!this.map) return;
    this.map.setStores((this.matches || []).filter(hasCoordinates));
    if (this.origin) {
      const nearest = this.matches.filter(hasCoordinates).slice(0, 5);
      this.map.fitStores([this.origin, ...nearest]);
    } else this.map.fitStores((this.matches || []).filter(hasCoordinates));
  }

  storeCard(store) {
    const card = this.node('article', 'gl-card');
    card.dataset.storeId = store.id;
    const heading = this.node('div', 'gl-card-head');
    const title = this.node('h3', 'gl-card-title');
    if (hasCoordinates(store)) {
      const button = this.node('button', 'gl-card-select', store.name);
      button.type = 'button';
      button.setAttribute('aria-label', `${store.name} — ${this.t('show_on_map')}`);
      button.addEventListener('click', () => {
        this.map?.focus(store.lat, store.lng, 14);
        this.map?.select?.(store.id);
        this.selectCard(store.id);
        if (matchMedia('(max-width: 749px)').matches) this.mapHost.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      title.append(button);
    } else title.textContent = store.name;
    heading.append(title);
    if (Number.isFinite(store.distance)) heading.append(this.node('span', 'gl-distance', this.t('distance', { km: new Intl.NumberFormat(this.dataset.locale, { maximumFractionDigits: 1 }).format(store.distance) })));
    card.append(heading);
    const tags = this.node('div', 'gl-tags');
    tags.append(this.node('span', `gl-tag ${store.kind === 'medimarket' ? 'gl-tag--medi' : ''}`, store.kind === 'medimarket' ? 'Medi-Market' : this.t('independent')));
    if (store.availableFrom && Date.now() < Date.parse(`${store.availableFrom}T00:00:00+02:00`)) tags.append(this.node('span', 'gl-tag gl-tag--date', this.t('available_from', { date: new Intl.DateTimeFormat(this.dataset.locale, { day: 'numeric', month: 'long', timeZone: 'Europe/Brussels' }).format(new Date(`${store.availableFrom}T12:00:00+02:00`)) })));
    if (store.visitType === 'appointment') tags.append(this.node('span', 'gl-tag', this.t('appointment')));
    if (store.visitType === 'pickup') tags.append(this.node('span', 'gl-tag', this.t('pickup')));
    card.append(tags);
    const address = this.node('p', 'gl-address');
    if (store.address) { address.append(document.createTextNode(store.address), document.createElement('br')); }
    address.append(document.createTextNode(`${store.postcode || ''} ${store.city} · ${store.country}`.trim()));
    card.append(address);
    const links = this.node('div', 'gl-card-links');
    if (store.address || hasCoordinates(store)) {
      const destination = store.address ? `${store.name}, ${store.address}, ${store.postcode || ''} ${store.city}, ${store.country}` : `${store.lat},${store.lng}`;
      const route = this.node('a', 'gl-route', this.t('directions'));
      route.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
      route.target = '_blank'; route.rel = 'noopener'; links.append(route);
    }
    const url = safeLink(store.url);
    if (url) { const website = this.node('a', '', this.t('store_details')); website.href = url; website.target = '_blank'; website.rel = 'noopener'; links.append(website); }
    if (store.phone && /^[+\d\s().-]+$/.test(store.phone)) { const phone = this.node('a', '', this.t('call')); phone.href = `tel:${store.phone.replace(/[^+\d]/g, '')}`; links.append(phone); }
    card.append(links);
    return card;
  }

  renderCards() {
    this.results.replaceChildren();
    for (const store of (this.matches || []).slice(0, this.limit)) this.results.append(this.storeCard(store));
    this.querySelector('[data-more]').hidden = (this.matches || []).length <= this.limit;
  }

  selectCard(id) {
    for (const card of this.results.children) card.classList.toggle('is-selected', card.dataset.storeId === id);
  }

  selectStore(id) {
    const index = this.matches.findIndex(store => store.id === id);
    if (index < 0) return;
    if (index >= this.limit) { this.limit = index + 1; this.renderCards(); }
    this.selectCard(id);
    const card = [...this.results.children].find(item => item.dataset.storeId === id);
    card?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
    this.map?.select?.(id);
  }

  renderOnline() {
    const stores = this.stores.filter(store => store.kind === 'online' && safeLink(store.url));
    this.online.hidden = !stores.length;
    const list = this.online.querySelector('[data-online-list]');
    list.replaceChildren();
    for (const store of stores) {
      const link = this.node('a', 'gl-online-link', store.name);
      link.href = safeLink(store.url); link.target = '_blank'; link.rel = 'noopener'; list.append(link);
    }
  }
}

if (!customElements.get('gentlee-store-locator')) customElements.define('gentlee-store-locator', GentleeStoreLocator);
